import {
  Container,
  GlobalStyles,
  styled,
  Typography,
  useTheme,
} from '@mui/material';

import { ChkIconVoltLogo } from '../icons/ChkIconVoltLogo';

import type { ChkPageWrapperProps } from './ChkPageWrapper.types';

export const ChkPageWrapper = ({ children, ...rest }: ChkPageWrapperProps) => {
  const {
    tokens,
    voltBranding: { poweredBy: isPoweredByVisible },
  } = useTheme();

  return (
    <ViewportContainer>
      <GlobalStyles
        styles={(theme) => ({
          body: {
            backgroundColor: tokens.bodyBackground,
            '& a:focus': {
              outlineColor: 'currentColor',
            },

            ...(theme.zoom.isZoomed && {
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              hyphens: 'auto',
            }),
          },
          mark: {
            backgroundColor: 'transparent',
            color: 'inherit',
            fontWeight: theme.typography.fontWeightBold,
          },
        })}
      />
      <PageContainer {...rest} maxWidth="xs" disableGutters>
        {children}
      </PageContainer>
      {isPoweredByVisible && (
        <FooterContainer>
          <StyledTypography variant="body2">Powered by</StyledTypography>
          <ChkIconVoltLogo aria-label="Volt" role="img" />
        </FooterContainer>
      )}
    </ViewportContainer>
  );
};

const ViewportContainer = styled('div')(({ theme }) => ({
  minHeight: ['100vh', '100dvh'],
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: `calc(100vw - 100%)`, // vw is not affected by scrollbars, so this prevents layout shifts when the scrollbar is shown; ref on #3: https://dev.to/rashidshamloo/preventing-the-layout-shift-caused-by-scrollbars-2flp

  [theme.breakpoints.up('xs')]: {
    paddingTop: theme.spacing(7),
  },
}));

const PageContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  background: 'white',
  display: 'flex',
  minHeight: ['100vh', '100dvh'],

  [theme.breakpoints.up('xs')]: {
    minHeight: '700px',
    borderRadius: theme.borders.borderRadius2,
    boxShadow: '0 4px 80px 0 rgba(31, 48, 60, 0.05)',
  },
}));

const FooterContainer = styled('footer')(({ theme }) => ({
  display: 'none',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  margin: theme.spacing(1, 'auto'),

  [theme.breakpoints.up('xs')]: {
    display: 'flex',
    margin: theme.spacing(8, 'auto'),
  },

  '& > svg': {
    color: theme.coreTokens.text.main,
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: '1.3rem',
  color: theme.coreTokens.text.main,
  opacity: 0.7,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '2rem',
  letterSpacing: 0,
}));
